import classNames from "classnames";
import * as React from "react";

import { useSetThreadPinned } from "@/shared/domain/agentThread/hooks";
import { Maximize2 } from "@/shared/foundation/icons";
import {
  MoreVertIcon,
  PushPinIcon,
  PushPinOutlinedIcon,
} from "@/shared/foundation/icons/mui";
import { Popover } from "@/shared/foundation/primitives";

import styles from "./ThreadRowMenu.module.css";

interface ThreadRowMenuProps {
  /** Thread the menu acts on. */
  threadId: string;

  /**
   * The signed-in user's `pinned_at` for this thread; null or undefined when
   * they have not pinned it.
   */
  pinnedAt?: string | null;

  /** Extra class for the host to control when the trigger is visible. */
  className?: string;

  /** Navigates to this thread's own page and closes the sidebar behind it —
   *  the same action and icon as the chat header's "Open in thread view"
   *  button. */
  onOpenThreadInFullView: (threadId: string) => void;
}

/**
 * Per-row overflow menu for a sidebar history thread: pin or unpin it, and
 * open its own page in the thread view.
 *
 * Sits inside the sidebar row's own click target, which opens the thread, so
 * every action here stops propagation to avoid also navigating.
 */
export const ThreadRowMenu = ({
  threadId,
  pinnedAt,
  className,
  onOpenThreadInFullView,
}: ThreadRowMenuProps) => {
  const [open, setOpen] = React.useState(false);
  const setPinned = useSetThreadPinned();
  const isPinned = pinnedAt !== undefined && pinnedAt !== null;

  const handleTogglePin = (event: React.MouseEvent) => {
    event.stopPropagation();
    setPinned.mutate({ threadId, pinned: !isPinned });
    setOpen(false);
  };

  const handleOpenFullPage = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(false);
    onOpenThreadInFullView(threadId);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Popover.Trigger
        render={
          <button
            type="button"
            aria-label="Thread actions"
            className={classNames(styles.trigger, className)}
            onClick={(event) => event.stopPropagation()}
          >
            <MoreVertIcon className={styles.triggerIcon} />
          </button>
        }
      />
      <Popover.Content
        align="end"
        aria-label="Thread actions"
        className={styles.popup}
      >
        <button
          type="button"
          className={styles.option}
          onClick={handleTogglePin}
        >
          {isPinned ? (
            <PushPinIcon className={styles.optionIcon} aria-hidden="true" />
          ) : (
            <PushPinOutlinedIcon
              className={styles.optionIcon}
              aria-hidden="true"
            />
          )}
          <span className={styles.optionLabel}>
            {isPinned ? "Unpin" : "Pin"}
          </span>
        </button>
        <button
          type="button"
          className={styles.option}
          onClick={handleOpenFullPage}
        >
          <Maximize2 className={styles.optionIcon} aria-hidden="true" />
          <span className={styles.optionLabel}>Open in thread view</span>
        </button>
      </Popover.Content>
    </Popover>
  );
};
